<script>
    import { onMount, tick } from "svelte";
    import app from "@/store/app.js";
    import user from "@/store/user.js";
    import tenant from "@/store/tenant.js";
    import routes from "./routes";
    import { wait, rememberShoppingReturn } from "@/assets/js/util";
    import { getTenantRedirectUrl, isOnSaaSRootDomain, loadTenantById } from "@/assets/js/tenant";
    import { setCanonical } from "@/assets/js/seo.js";
    
    let Page;
    let history = {};
    let current = "";
    let mounted = false;

    $: if (mounted) push($app.url);
    async function push(path) {
        if (current == path || !mounted) return;
        
        const [pathPart, queryPart] = path.split("?");
        
        const staticFileExtensions = ['.xml', '.txt', '.html', '.json', '.ico', '.png', '.jpg', '.jpeg', '.webp', '.svg', '.css', '.js', '.pdf'];
        const isStaticFile = staticFileExtensions.some(ext => pathPart.toLowerCase().endsWith(ext));
        if (isStaticFile) {
            const fullUrl = window.location.origin + path;
            window.location.replace(fullUrl);
            return;
        }

        const url = convertToWild(pathPart);

        // Staff never land on the hub — send them straight to the team portal.
        if (url === "/hub" && $user.id && $user.role !== "admin") {
            return push("/team-portal");
        }

        const authRoute = routes[url];
        if (authRoute && authRoute.auth && $user.id && isOnSaaSRootDomain()) {
            if (!$tenant.slug && $user.tenant) {
                await loadTenantById($user.tenant);
            }
            if ($tenant.slug) {
                const fullPath = queryPart ? `${pathPart}?${queryPart}` : pathPart;
                const targetBase = getTenantRedirectUrl($tenant.slug);
                const targetUrl = targetBase + fullPath;
                window.location.replace(targetUrl);
                return;
            }
        }
        
        await beforeRouteChange($app.url, current);
        if (!routes[url]?.page) return push("/404");
        if (path == current && Page) return;
        if (guard(url)) return push("/permission-denied");
        rememberShoppingReturn(current, path);
        const scroller = document.querySelector('[data-scroll-container]');
        const sx = scroller ? scroller.scrollLeft : window.scrollX;
        const sy = scroller ? scroller.scrollTop : window.scrollY;
        history[current] = { y: sy, x: sx };
        current = path;
        const module = await import(`../views/${routes[url]?.page}.svelte`);
        Page = module.default;
        await tick();
        const scroller2 = document.querySelector('[data-scroll-container]');
        const hx = history[path]?.x || 0;
        const hy = history[path]?.y || 0;
        if (scroller2) scroller2.scrollTo(hx, hy); else window.scrollTo(hx, hy);
        // Run a second restoration after layout/data settle to handle list re-render after edits
        await tick();
        await wait(0);
        const scroller3 = document.querySelector('[data-scroll-container]');
        if (scroller3) scroller3.scrollTo(hx, hy); else window.scrollTo(hx, hy);
        if (Page !== undefined) {
            const fullPath = queryPart ? `${pathPart}?${queryPart}` : pathPart;
            const currentFull = window.location.pathname + (window.location.search || "");
            if (currentFull !== fullPath) {
                window.history.pushState(null, null, fullPath);
            }
        }
        const siteName = "Jungle House Dispensary";
        const routeName = routes[url]?.name || "";
        if (url === "/product/*") {
            // Product page handles its own SEO via setProductSeo()
        } else if (url === "/blog/*" || url === "/blog" || url === "/about" || url === "/find-us" || url === "/weed-delivery-rawai" || url === "/weed-shop-rawai" || url === "/weed-delivery-phuket") {
            // Blog, About, Find Us & keyword landing pages handle their own SEO
        } else if (url === "/") {
            document.title = `${siteName} | Weed Shop & Delivery in Rawai, Phuket`;
            setCanonical("https://junglehouse.co.th/");
        } else if (url === "/products" || url === "/products/*") {
            // Menu pages handle their own SEO via setMenuSeo() (per-category
            // title, canonical and ItemList structured data). Path-based
            // category URLs canonicalize to /products?category=X so Google
            // consolidates the duplicate routes.
        } else {
            document.title = `${routeName} | ${siteName}`;
            setCanonical("https://junglehouse.co.th" + ($app.url || "/").split("?")[0]);
        }
        hijackAnchors();
        observeAnimate();
    }

    function handleHover() {
        const url = convertToWild(this.pathname);
        const view = routes[url]?.page;
        if (view) return import(`../views/${view}.svelte`);
    }
    async function beforeRouteChange(to, from) {
        if ($app.drawer) {
            $app.drawer = false;
            return await wait(300);
        }
        if (!to.includes('product') && from.includes('product')) {
            $app.activeItem = {}
        }
    }
    function handleClick(e) {
        e.preventDefault();
        const fullUrl = this.pathname + this.search;
        pushURLToStore(fullUrl);
    }
    // Storefront shoppers (role="customer") may only reach customer-facing
    // authed routes. Every other auth:true route is staff/admin surface (POS,
    // /admin/*, sales, inventory, ...) and most carry no explicit role, so
    // without this a logged-in customer would pass their auth check. Keys are
    // wild-carded route forms (see convertToWild), matching routes[path] lookup.
    const CUSTOMER_ALLOWED_AUTH_ROUTES = new Set([
        "/account",
    ]);

    function guard(path) {
        const route = routes[path];

        if (!route) {
            console.warn("router.guard missing route", path);
            return true;
        }

        if (route.auth && !$user.id) {
            console.warn("router.guard blocking unauthenticated access", { path, route, user: $user });
            return true;
        }

        if (route.auth && $user.role === "customer" && !CUSTOMER_ALLOWED_AUTH_ROUTES.has(path)) {
            console.warn("router.guard blocking customer from staff/admin route", { path, user: $user });
            return true;
        }

        if (route.role === "admin" && $user.role !== "admin") {
            console.warn("router.guard blocking non-admin for admin route", { path, route, user: $user });
            return true;
        }

        return false;
    }
    function convertURL(str) {
        str = str.split("/").map((el) => (el.startsWith(":") ? "*" : el));
        return str.join("/");
    }
    function convertToWild(url) {
        for (let oldKey of Object.keys(routes)) {
            let ary1 = url.split("/");
            let ary2 = convertURL(oldKey).split("/");
            if (ary1.length !== ary2.length) continue;
            for (let x in ary1) {
                if (ary2[x] == "*") ary1[x] = "*";
            }
            if (JSON.stringify(ary1) !== JSON.stringify(ary2)) continue;
            return ary1.join("/");
        }
    }
    function convertToDynamicRoutes() {
        for (let oldKey of Object.keys(routes)) {
            let newKey = convertURL(oldKey);
            routes[newKey] = routes[oldKey];
            if (oldKey == newKey) continue;
            delete routes[oldKey];
        }
    }
    function observeAnchors() {
        const observer = new MutationObserver(hijackAnchors);
        const elementNode = document.getElementById("app");
        let options = { attributes: false, childList: true, subtree: true };
        observer.observe(elementNode, options);
    }
    function observeAnimate() {
        const observer = new IntersectionObserver(animate, { threshold: 0 });
        let classes = ".animate, .animate-left, .animate-right";
        const animation = document.querySelectorAll(classes);
        animation.forEach((el) => observer.observe(el));
    }
    function hijackAnchors() {
        const parsedUrl1 = new URL(window.location.origin);
        var els = document.getElementsByTagName("a");
        for (let a of els) {
            let parsedUrl2 = new URL(a.href || window.location.origin);
            let same = parsedUrl1.hostname === parsedUrl2.hostname;
            if (same) a.addEventListener("click", handleClick);
            if (same) a.addEventListener("mouseover", handleHover);
        }
    }
    function animate(entries) {
        entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add("scroll-animation");
        });
    }
    function pushURLToStore(url) {
        const fullUrl = url || window.location.pathname + window.location.search;
        const [pathPart] = fullUrl.split("?");
        
        const staticFileExtensions = ['.xml', '.txt', '.html', '.json', '.ico', '.png', '.jpg', '.jpeg', '.webp', '.svg', '.css', '.js', '.pdf'];
        const isStaticFile = staticFileExtensions.some(ext => pathPart.toLowerCase().endsWith(ext));
        
        if (isStaticFile) {
            return;
        }
        
        $app.url = fullUrl;
    }

    onMount(() => {
        mounted = true;
        pushURLToStore();
        window.history.scrollRestoration = "manual";
        window.onpopstate = async () => pushURLToStore();
        convertToDynamicRoutes();
        observeAnimate();
        observeAnchors();
    });
</script>

<svelte:component this={Page} />